import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    CustomInput,
    Label,
    Row,
    Col,
    Input,
    Form,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    CardFooter
} from 'reactstrap';
import Swal from 'sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';
import { useParams, useHistory } from 'react-router-dom';

import toolsService from 'services/tools.service';
import downloadService from 'services/download.service';
import { usePageContext } from 'contexts/PageContext';

// Lấy URL API từ biến môi trường hoặc giá trị mặc định
const API_URL = process.env.REACT_APP_API_URL || "https://api.dhbtools.com/admin";

function ToolDetails() {
    const { matool } = useParams();
    const history = useHistory();
    const { setBreadcrumb, clearBreadcrumb } = usePageContext();
    const [loading, setLoading] = useState(false);
    const [tool, setTool] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [description, setDescription] = useState('');
    const [descriptionLoading, setDescriptionLoading] = useState(false);
    const maxDescriptionLength = 500; // Giới hạn ký tự cho mô tả

    // Log khi component mount và nhận params
    console.log('ToolDetails mounted with matool:', matool);

    // Quản lý thuộc tính
    const [attributes, setAttributes] = useState([]);

    // Quản lý biến thể
    const [variations, setVariations] = useState([]);

    // Modal thêm/sửa thuộc tính
    const [attributeModal, setAttributeModal] = useState(false);
    const [attributeName, setAttributeName] = useState('');
    const [attributeValues, setAttributeValues] = useState('');
    const [editAttributeIndex, setEditAttributeIndex] = useState(-1);

    // Định dạng số tiền VND
    const formatCurrency = (amount) => {
        if (!amount) return '0';
        return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // Chuyển đổi từ định dạng VND sang số nguyên để lưu trữ
    const parseCurrency = (formattedAmount) => {
        if (!formattedAmount) return '0';
        return formattedAmount.toString().replace(/\./g, '');
    };

    // Tính số ngày từ thời hạn
    const calculateDaysFromDuration = (duration) => {
        if (!duration) return 0;

        // Lấy giá trị số từ thời hạn
        const numericValue = parseInt(duration.match(/\d+/)?.[0] || 0);

        if (duration.includes('tháng')) {
            return numericValue * 30;
        } else if (duration.includes('năm')) {
            return numericValue * 365;
        } else if (duration.toLowerCase().includes('vĩnh viễn')) {
            return 10 * 365; // Vĩnh viễn = 10 năm
        }

        return 0;
    };

    useEffect(() => {
        if (matool) {
            console.log('Loading tool details for matool:', matool);
            loadToolDetails();
            loadThumbnail();
        }

        // Cleanup: clear breadcrumb when component unmounts
        return () => {
            clearBreadcrumb();
        };
    }, [matool]);

    // Tải thumbnail từ API
    const loadThumbnail = async () => {
        try {
            console.log('Bắt đầu tải thumbnail cho matool:', matool);

            // Tạo URL trực tiếp để hiển thị ảnh
            // Sử dụng timestamp để tránh cache
            const timestamp = new Date().getTime();
            const imageUrl = `${API_URL}/tools/${matool}/thumbnail?t=${timestamp}`;

            console.log('URL ảnh thumbnail:', imageUrl);

            // Cập nhật state với URL ảnh
            setThumbnailPreview(imageUrl);
        } catch (error) {
            console.error('Lỗi khi tải thumbnail:', error);
            // Trong trường hợp lỗi, vẫn cố gắng hiển thị ảnh từ URL trực tiếp với timestamp mới
            const timestamp = new Date().getTime();
            setThumbnailPreview(`${API_URL}/tools/${matool}/thumbnail?t=${timestamp}`);
        }
    };

    // Tải thông tin chi tiết công cụ và biến thể
    const loadToolDetails = async () => {
        try {
            console.log('Start loading tool details');
            setLoading(true);

            // Tải thông tin công cụ
            const response = await toolsService.getToolById(matool);
            console.log('API response:', response);

            if (!response || !response.success || !response.tool) {
                Swal.fire({
                    title: 'Lỗi!',
                    text: 'Không tìm thấy thông tin công cụ',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
                history.push('/admin/tools');
                return;
            }

            const toolData = response.tool;
            setTool(toolData);

            // Cập nhật giá trị description
            setDescription(toolData.description || '');

            // Set breadcrumb
            setBreadcrumb(
                [
                    { label: 'Tool Management', to: '/admin/tools' },
                    { label: toolData.name }
                ],
                '/admin/tools'
            );

            // Xử lý biến thể
            if (toolData && toolData.variations) {
                processToolVariations(toolData);
            } else {
                console.log('Không có dữ liệu biến thể');
                setAttributes([]);
                setVariations([]);
            }
        } catch (error) {
            console.error('Lỗi khi tải dữ liệu công cụ:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: `Không thể tải dữ liệu công cụ: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'OK'
            });
            history.push('/admin/tools');
        } finally {
            setLoading(false);
        }
    };

    // Xử lý dữ liệu biến thể
    const processToolVariations = (toolData) => {
        console.log('Xử lý biến thể:', toolData.variations);
        // Lấy dữ liệu variations từ tool được chọn
        const toolVariations = toolData.variations || [];

        if (toolVariations.length === 0) {
            // Nếu không có dữ liệu variations, đặt mảng rỗng và không hiển thị gì
            setAttributes([]);
            setVariations([]);
            return;
        }

        // Trích xuất các attributes từ variations
        const extractedAttributes = {};

        // Lặp qua tất cả variations để thu thập các thuộc tính và giá trị duy nhất
        toolVariations.forEach(variation => {
            if (variation.attributes) {
                Object.entries(variation.attributes).forEach(([attrName, attrValue]) => {
                    if (!extractedAttributes[attrName]) {
                        extractedAttributes[attrName] = new Set();
                    }
                    extractedAttributes[attrName].add(attrValue);
                });
            }
        });

        // Chuyển đổi từ object sang mảng attributes
        const attributesArray = Object.entries(extractedAttributes).map(([name, valuesSet]) => ({
            name,
            values: Array.from(valuesSet)
        }));

        console.log('Attributes trích xuất:', attributesArray);
        setAttributes(attributesArray);

        // Sử dụng variations từ API trực tiếp
        setVariations(toolVariations);
        console.log('Đã đặt variations:', toolVariations);
    };

    // Hiển thị modal thêm/sửa thuộc tính
    const toggleAttributeModal = (index = -1) => {
        setAttributeModal(!attributeModal);

        if (index >= 0 && attributes[index]) {
            // Chế độ sửa
            const attr = attributes[index];
            setAttributeName(attr.name);
            setAttributeValues(attr.values.join(', '));
            setEditAttributeIndex(index);
        } else {
            // Chế độ thêm mới
            setAttributeName('');
            setAttributeValues('');
            setEditAttributeIndex(-1);
        }
    };

    // Thêm/sửa thuộc tính
    const saveAttribute = () => {
        if (!attributeName || !attributeValues) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Vui lòng nhập tên thuộc tính và các giá trị',
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
            return;
        }

        // Chuyển đổi chuỗi giá trị thành mảng
        const values = attributeValues.split(',').map(value => value.trim()).filter(value => value !== '');

        if (values.length === 0) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Vui lòng nhập ít nhất một giá trị cho thuộc tính',
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
            return;
        }

        const newAttribute = {
            name: attributeName,
            values: values
        };

        let newAttributes = [...attributes];

        if (editAttributeIndex >= 0) {
            // Chế độ sửa
            newAttributes[editAttributeIndex] = newAttribute;
        } else {
            // Chế độ thêm mới
            newAttributes.push(newAttribute);
        }

        setAttributes(newAttributes);
        setAttributeModal(false);

        // Cập nhật lại biến thể với thuộc tính mới
        generateVariationsFromAttributes(newAttributes);
    };

    // Tạo tất cả các biến thể có thể từ các thuộc tính
    const generateVariationsFromAttributes = (attrs) => {
        if (!attrs || attrs.length === 0) {
            setVariations([]);
            return;
        }

        console.log('Tạo biến thể từ thuộc tính:', attrs);

        // Lưu trữ các biến thể hiện tại để giữ lại giá trị giá và trạng thái
        const currentVariations = [...variations];

        // Hàm đệ quy để tạo tất cả các kết hợp có thể
        const generateCombinations = (attrs, index = 0, current = {}) => {
            if (index === attrs.length) {
                // Đạt đến cuối cùng của thuộc tính, thêm kết hợp hiện tại vào kết quả
                return [current];
            }

            const attribute = attrs[index];
            let combinations = [];

            for (const value of attribute.values) {
                const newCurrent = { ...current };
                newCurrent[attribute.name] = value;
                combinations = [...combinations, ...generateCombinations(attrs, index + 1, newCurrent)];
            }

            return combinations;
        };

        // Tạo tất cả các kết hợp
        const combinations = generateCombinations(attrs);
        console.log('Tất cả các kết hợp có thể:', combinations);

        // Tạo biến thể với kết hợp và giá (mặc định hoặc từ dữ liệu hiện có)
        const newVariations = combinations.map(combination => {
            // Tạo key từ kết hợp để so sánh với dữ liệu hiện có
            const key = Object.entries(combination)
                .map(([attr, value]) => `${attr}:${value}`)
                .sort()
                .join('|');

            // Tìm trong dữ liệu hiện có
            const existingVariation = currentVariations.find(v => {
                if (!v.attributes) return false;

                const existingKey = Object.entries(v.attributes)
                    .map(([attr, value]) => `${attr}:${value}`)
                    .sort()
                    .join('|');
                return existingKey === key;
            });

            // Tìm thuộc tính thời hạn
            const durationValue = Object.entries(combination).find(
                ([key, value]) => key.toLowerCase().includes('thời hạn') || key.toLowerCase().includes('hạn')
            )?.[1];

            // Tự động tính số ngày dựa trên thời hạn nếu không có sẵn
            let days = existingVariation ? existingVariation.days : 0;
            if (!days && durationValue) {
                days = calculateDaysFromDuration(durationValue);
            }

            // Tạo biến thể mới hoặc giữ lại giá trị hiện có
            return {
                attributes: combination,
                price: existingVariation ? existingVariation.price : '0',
                days: days,
                enabled: existingVariation ? existingVariation.enabled : true
            };
        });

        console.log('Biến thể mới tạo:', newVariations);
        setVariations(newVariations);
    };

    // Xóa thuộc tính
    const deleteAttribute = (index) => {
        Swal.fire({
            title: 'Xác nhận xóa?',
            text: 'Bạn có chắc chắn muốn xóa thuộc tính này? Tất cả biến thể liên quan sẽ bị xóa.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then((result) => {
            if (result.isConfirmed) {
                const newAttributes = [...attributes];
                newAttributes.splice(index, 1);
                setAttributes(newAttributes);

                // Tạo lại các biến thể với thuộc tính mới
                generateVariationsFromAttributes(newAttributes);
            }
        });
    };

    // Bật/tắt biến thể
    const toggleVariationStatus = (index) => {
        const updatedVariations = [...variations];
        updatedVariations[index].enabled = !updatedVariations[index].enabled;
        setVariations(updatedVariations);
    };

    // Xóa biến thể
    const deleteVariation = (index) => {
        Swal.fire({
            title: 'Xác nhận xóa?',
            text: 'Bạn có chắc chắn muốn xóa biến thể này?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then((result) => {
            if (result.isConfirmed) {
                // Lấy thông tin biến thể để hiển thị
                const variationToDelete = variations[index];
                let variationInfo = '';

                if (variationToDelete && variationToDelete.attributes) {
                    variationInfo = Object.entries(variationToDelete.attributes)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ');
                }

                // Xóa biến thể khỏi mảng
                const updatedVariations = [...variations];
                updatedVariations.splice(index, 1);
                setVariations(updatedVariations);

                // Thông báo đã xóa thành công
                Swal.fire(
                    'Đã xóa!',
                    variationInfo ? `Đã xóa biến thể: ${variationInfo}` : 'Đã xóa biến thể thành công',
                    'success'
                );
            }
        });
    };

    // Lưu tất cả dữ liệu lên máy chủ
    const saveAll = async () => {
        if (!tool) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Không tìm thấy thông tin công cụ',
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
            return;
        }

        try {
            setLoading(true);
            // Chuẩn bị dữ liệu để lưu
            // Chỉ cần gửi mảng variations, API sẽ tự xử lý

            // Đảm bảo tất cả các variations có đủ thông tin cần thiết
            const validatedVariations = variations.map(v => ({
                attributes: v.attributes,
                days: v.days || 0,
                price: v.price || '0',
                enabled: v.enabled !== undefined ? v.enabled : true
            }));

            console.log('Dữ liệu gửi lên:', validatedVariations);

            // Gọi API để cập nhật
            const response = await toolsService.updateVariations(tool.matool, {
                variations: validatedVariations
            });

            if (response && response.success) {
                Swal.fire({
                    title: 'Thành công!',
                    text: 'Đã lưu dữ liệu thành công',
                    icon: 'success',
                    confirmButtonText: 'Đồng ý'
                });

                // Tải lại dữ liệu sau khi lưu thành công
                loadToolDetails();
            } else {
                throw new Error('Lỗi khi lưu dữ liệu');
            }
        } catch (error) {
            console.error('Error saving data:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: 'Không thể lưu dữ liệu: ' + (error.message || 'Lỗi không xác định'),
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
        } finally {
            setLoading(false);
        }
    };

    // Xử lý thay đổi file thumbnail
    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Kiểm tra file có phải là ảnh không
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Vui lòng chọn file ảnh',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Hiển thị preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setThumbnailPreview(e.target.result);
        };
        reader.readAsDataURL(file);

        // Lưu file để upload
        setThumbnailFile(file);
    };

    // Upload thumbnail
    const uploadThumbnail = async () => {
        if (!thumbnailFile) {
            Swal.fire({
                title: 'Thông báo',
                text: 'Vui lòng chọn ảnh trước khi cập nhật',
                icon: 'info',
                confirmButtonText: 'OK'
            });
            return;
        }

        try {
            setUploadLoading(true);
            console.log('Bắt đầu tải lên thumbnail cho matool:', matool);

            // Tạo FormData để gửi file
            const formData = new FormData();
            formData.append('thumbnail', thumbnailFile);
            console.log('File đã chọn:', thumbnailFile.name, thumbnailFile.type, thumbnailFile.size);

            // Gọi API upload thumbnail
            const response = await toolsService.updateThumbnail(matool, formData);
            console.log('Phản hồi API upload:', response);

            if (response && response.success) {
                Swal.fire({
                    title: 'Thành công!',
                    text: 'Đã cập nhật ảnh thumbnail thành công',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });

                // Tạo URL mới với timestamp để tránh cache
                const timestamp = new Date().getTime();
                const newThumbnailUrl = `${API_URL}/tools/${matool}/thumbnail?t=${timestamp}`;
                console.log('URL ảnh mới sau khi cập nhật:', newThumbnailUrl);

                // Cập nhật state với URL mới
                setThumbnailPreview(newThumbnailUrl);
                setThumbnailFile(null);
            } else {
                throw new Error(response?.message || 'Lỗi không xác định');
            }
        } catch (error) {
            console.error('Lỗi khi upload thumbnail:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: `Không thể cập nhật thumbnail: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setUploadLoading(false);
        }
    };

    // Lưu mô tả
    const saveDescription = async () => {
        if (!tool) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Không tìm thấy thông tin công cụ',
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
            return;
        }

        try {
            setDescriptionLoading(true);
            console.log('Bắt đầu cập nhật mô tả cho matool:', matool);

            // Gọi API để cập nhật mô tả
            const response = await toolsService.updateDescription(matool, description);
            console.log('Phản hồi API cập nhật mô tả:', response);

            if (response && response.success) {
                Swal.fire({
                    title: 'Thành công!',
                    text: 'Đã cập nhật mô tả thành công',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });

                // Cập nhật tool state để hiển thị mô tả mới
                setTool({
                    ...tool,
                    description: description
                });
            } else {
                throw new Error(response?.message || 'Lỗi không xác định');
            }
        } catch (error) {
            console.error('Lỗi khi cập nhật mô tả:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: `Không thể cập nhật mô tả: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setDescriptionLoading(false);
        }
    };

    // Quay lại trang danh sách công cụ
    const goBack = () => {
        history.push('/admin/tools');
    };

    if (loading) {
        return (
            <div className="content">
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Đang tải...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!tool) {
        return (
            <div className="content">
                <Card>
                    <CardBody>
                        <div className="text-center p-5">
                            <h3>Không tìm thấy thông tin công cụ</h3>
                            <Button color="primary" onClick={goBack}>
                                Quay lại danh sách
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div className="content">

            <Row>
                <Col md="6">
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '15px', marginBottom: '20px' }}>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold', marginLeft: '10px' }}>
                                {tool.name}
                            </Label>
                        </CardHeader>
                        <CardBody>
                            <Row>
                                <Col xs="12">
                                    <Label style={{ fontSize: '16px', color: 'DimGray' }}>
                                        Ảnh
                                    </Label>
                                    <div className="d-flex flex-column align-items-center mb-4" style={{ border: '1px solid #ddd', padding: '20px', borderRadius: '8px' }}>
                                        {thumbnailPreview ? (
                                            <div style={{ position: 'relative', width: '200px', height: '200px', marginBottom: '15px' }}>
                                                <img
                                                    src={thumbnailPreview}
                                                    alt="Thumbnail"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        borderRadius: '4px'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://via.placeholder.com/200x200?text=No+Image';
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ width: '200px', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', marginBottom: '15px', borderRadius: '4px' }}>
                                                <span className="text-muted">Chưa có ảnh</span>
                                            </div>
                                        )}

                                        <div className="d-flex flex-column w-100">
                                            <input
                                                type="file"
                                                id="thumbnail-upload"
                                                accept="image/*"
                                                className="d-none"
                                                onChange={handleThumbnailChange}
                                            />
                                            <div className="d-flex justify-content-center">
                                                <Button
                                                    color="secondary"
                                                    outline
                                                    className="mr-2"
                                                    onClick={() => document.getElementById('thumbnail-upload').click()}
                                                >
                                                    <i className="nc-icon nc-image mr-1"></i> Chọn ảnh
                                                </Button>
                                                <Button
                                                    color="primary"
                                                    disabled={!thumbnailFile || uploadLoading}
                                                    onClick={uploadThumbnail}
                                                >
                                                    {uploadLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                                            Đang tải lên...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="nc-icon nc-cloud-upload-94 mr-1"></i> Cập nhật
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                            <small className="text-muted text-center mt-2">
                                                Định dạng hỗ trợ: JPG, PNG. Kích thước tối đa: 2MB
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs="12">
                                    <Label style={{ fontSize: '16px', color: 'DimGray' }}>
                                        Mô tả công cụ
                                    </Label>
                                    <div className="d-flex flex-column mb-3">
                                        <FormGroup>
                                            <Input
                                                type="textarea"
                                                id="description"
                                                placeholder="Nhập mô tả chi tiết về công cụ..."
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value.slice(0, maxDescriptionLength))}
                                                rows={5}
                                                style={{
                                                    resize: 'vertical',
                                                    minHeight: '120px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <div className="d-flex justify-content-between mt-1">
                                                <small className="text-muted">
                                                    Mô tả ngắn gọn về chức năng của công cụ
                                                </small>
                                                <small className={`${description.length >= maxDescriptionLength ? 'text-danger' : 'text-muted'}`}>
                                                    {description.length}/{maxDescriptionLength} ký tự
                                                </small>
                                            </div>
                                        </FormGroup>
                                        <div className="d-flex justify-content-end">
                                            <Button
                                                color="primary"
                                                size="sm"
                                                disabled={descriptionLoading}
                                                onClick={saveDescription}
                                            >
                                                {descriptionLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                                        Đang lưu...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="nc-icon nc-check-2 mr-1"></i> Lưu mô tả
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Label style={{ fontSize: '16px', color: 'DimGray' }}>
                                    Thông tin tải xuống
                                </Label>
                                <Table bordered>
                                    <tbody>
                                        <tr>
                                            <td width="150"><b>Mã công cụ</b></td>
                                            <td>{tool.matool}</td>
                                        </tr>
                                        <tr>
                                            <td><b>Tệp tin</b></td>
                                            <td>{tool.file}</td>
                                        </tr>
                                        <tr>
                                            <td><b>Mật khẩu giải nén</b></td>
                                            <td>{tool.passunrar}</td>
                                        </tr>
                                        <tr>
                                            <td><b>Người tải lên</b></td>
                                            <td>{tool.uploadedBy}</td>
                                        </tr>
                                        <tr>
                                            <td><b>Xác thực tải về</b></td>
                                            <td>
                                                <CustomInput
                                                    type="switch"
                                                    id="auth-switch"
                                                    checked={!!tool.downloadRequireAuth}
                                                    label={tool.downloadRequireAuth ? "Có" : "Không"}
                                                    disabled
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Row>
                        </CardBody>
                        <CardFooter className="text-right bg-transparent border-0">
                            <Button color="primary" onClick={saveAll}><i className="nc-icon nc-check-2 mr-1"></i> Lưu thông tin</Button>
                        </CardFooter>
                    </Card>
                </Col>
                <Col md="6">
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '15px', marginBottom: '20px' }}>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <div>


                            </div>
                        </CardHeader>
                        <CardBody>
                            <Row>

                                {/* Phần quản lý thuộc tính */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="m-0">Thuộc tính</h5>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => toggleAttributeModal()}
                                    >
                                        <i className="nc-icon nc-simple-add mr-1"></i> Thêm thuộc tính
                                    </Button>
                                </div>

                                {attributes.length === 0 ? (
                                    <div className="text-center text-muted">
                                        Chưa có thuộc tính nào được tạo
                                    </div>
                                ) : (
                                    <Table className="table-hover" style={{ marginTop: '10px', lineHeight: '15px' }}>
                                        <thead className="text-primary">
                                            <tr>
                                                <th>Tên thuộc tính</th>
                                                <th>Giá trị</th>
                                                <th>Thao tác</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {attributes.map((attribute, index) => (
                                                <tr key={index}>
                                                    <td><b>{attribute.name}</b></td>
                                                    <td>{attribute.values.join(', ')}</td>
                                                    <td>
                                                        <Button
                                                            color="warning"
                                                            size="sm"
                                                            onClick={() => toggleAttributeModal(index)}
                                                        >
                                                            <i className="nc-icon nc-ruler-pencil"></i> Sửa
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            className="ml-2"
                                                            onClick={() => deleteAttribute(index)}
                                                        >
                                                            <i className="nc-icon nc-simple-remove"></i> Xóa
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Row>
                            <Row>
                                {/* Phần biến thể */}
                                <h5 className="mb-3 mt-4">Biến thể ({variations.length})</h5>

                                {variations.length === 0 ? (
                                    <div className="text-center text-muted">
                                        Không có biến thể nào. Vui lòng thêm thuộc tính trước.
                                    </div>
                                ) : (
                                    <>
                                        <Table className="table-hover" style={{ marginTop: '10px', lineHeight: '15px' }}>
                                            <thead className="text-primary">
                                                <tr>
                                                    <th>Kích hoạt</th>
                                                    {attributes.map((attr, index) => (
                                                        <th key={index}>{attr.name}</th>
                                                    ))}
                                                    <th>Ngày</th>
                                                    <th>Giá</th>
                                                    <th>Thao tác</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {variations.map((variation, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <CustomInput
                                                                type="switch"
                                                                id={`switch-${index}`}
                                                                checked={variation.enabled}
                                                                onChange={() => toggleVariationStatus(index)}
                                                            />
                                                        </td>
                                                        {attributes.map((attr, attrIndex) => (
                                                            <td key={attrIndex}>
                                                                {variation.attributes[attr.name]}
                                                            </td>
                                                        ))}
                                                        <td>
                                                            <Input
                                                                type="number"
                                                                value={variation.days || 0}
                                                                onChange={(e) => {
                                                                    const updatedVariations = [...variations];
                                                                    updatedVariations[index].days = parseInt(e.target.value) || 0;
                                                                    setVariations(updatedVariations);
                                                                }}
                                                                className="text-right"
                                                                style={{ width: "70px" }}
                                                                min="0"
                                                            />
                                                        </td>
                                                        <td>
                                                            <InputGroup size="sm">
                                                                <Input
                                                                    type="text"
                                                                    value={formatCurrency(variation.price)}
                                                                    onChange={(e) => {
                                                                        // Chỉ cho phép số và dấu chấm
                                                                        const value = e.target.value.replace(/[^\d.]/g, '');
                                                                        const updatedVariations = [...variations];
                                                                        updatedVariations[index].price = parseCurrency(value);
                                                                        setVariations(updatedVariations);
                                                                    }}
                                                                    className="text-right"
                                                                    style={{ minWidth: "100px" }}
                                                                />
                                                                <InputGroupAddon addonType="append">
                                                                    <InputGroupText style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        padding: '0 8px',
                                                                        height: '100%'
                                                                    }}>
                                                                        <span style={{
                                                                            fontFamily: 'Arial',
                                                                            fontSize: '14px',
                                                                            lineHeight: 1
                                                                        }}>VNĐ</span>
                                                                    </InputGroupText>
                                                                </InputGroupAddon>
                                                            </InputGroup>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => deleteVariation(index)}
                                                            >
                                                                <i className="nc-icon nc-simple-remove"></i> Xóa
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>

                                        {/* Footer với nút lưu */}
                                        <div className="d-flex justify-content-end mt-3">
                                            <Button
                                                color="success"
                                                onClick={saveAll}
                                            >
                                                Lưu biến thể
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>



            {/* Modal thêm/sửa thuộc tính */}
            <Modal isOpen={attributeModal} toggle={() => toggleAttributeModal()}>
                <ModalHeader toggle={() => toggleAttributeModal()}>
                    {editAttributeIndex >= 0 ? 'Sửa thuộc tính' : 'Thêm thuộc tính mới'}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="attributeName">Tên thuộc tính</Label>
                            <Input
                                type="text"
                                id="attributeName"
                                placeholder="VD: Màu sắc, Kích thước..."
                                value={attributeName}
                                onChange={(e) => setAttributeName(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="attributeValues">Giá trị (phân cách bằng dấu phẩy)</Label>
                            <Input
                                type="textarea"
                                id="attributeValues"
                                placeholder="VD: Đỏ, Xanh, Vàng..."
                                value={attributeValues}
                                onChange={(e) => setAttributeValues(e.target.value)}
                            />
                            <small className="form-text text-muted">
                                Nhập các giá trị phân cách bằng dấu phẩy
                            </small>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => toggleAttributeModal()}><i className="nc-icon nc-simple-remove mr-1"></i> Hủy</Button>
                    <Button color="primary" onClick={saveAttribute}>
                        {editAttributeIndex >= 0 ? 'Cập nhật' : 'Thêm'}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default ToolDetails; 